/**
 * Step 10: Generate & Send
 * Generate the lease document and send for signing
 */

import { useNavigate, Link } from 'react-router-dom';
import type { WizardState } from '../../../types/lease-generation';
import { Button } from '../../../components/ui/button';
import { leaseDocumentPdfUrl, type DisclosureReadinessResponse } from '../../../lib/api';
import { formatDateTime } from '../../../utils/date';

interface Step10Props {
  state: WizardState;
  generateLease: () => Promise<void>;
  sendForSigning: () => Promise<void>;
  saveDraft: () => Promise<void>;
  disclosureReadiness?: DisclosureReadinessResponse | null;
  unitId?: string;
}

export function Step10GenerateSend({ state, generateLease, sendForSigning, saveDraft, disclosureReadiness, unitId }: Step10Props) {
  const navigate = useNavigate();
  const { generatedDocument, isLoading, isSaving, error, conflictCode } = state;

  const disclosuresBlocking = disclosureReadiness !== null &&
    disclosureReadiness !== undefined &&
    !disclosureReadiness.ready;

  const handleSaveDraftAndExit = async () => {
    await saveDraft();
    navigate(`/units/${unitId}`);
  };

  const documentStatus = generatedDocument?.status;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Generate & Send</h3>
        <p className="text-sm text-muted-foreground">
          Generate the lease document and optionally send it for digital signatures.
        </p>
      </div>

      {/* Disclosure readiness blocker */}
      {disclosuresBlocking && !generatedDocument && (
        <div
          data-testid="disclosure-readiness-blocker"
          className="p-4 bg-destructive/5 border border-destructive/30 rounded-lg flex flex-col gap-2"
          role="alert"
        >
          <p className="text-sm font-semibold text-destructive m-0">Required disclosures are incomplete</p>
          <p className="text-sm text-destructive m-0">
            The following disclosures must be completed before generating a lease:{' '}
            <strong>
              {disclosureReadiness!.incompleteDisclosureIds
                .map(id => id.replace(/_/g, ' '))
                .join(', ')}
            </strong>
          </p>
          {unitId && (
            <Link
              to={`/units/${unitId}/disclosures`}
              data-testid="disclosure-blocker-manage-link"
              className="text-sm font-semibold text-destructive underline"
            >
              Manage Disclosures →
            </Link>
          )}
        </div>
      )}

      {/* Tenant conflict banner */}
      {conflictCode === 'TENANT_ALREADY_ON_ACTIVE_LEASE' && !generatedDocument && (
        <div data-testid="tenant-conflict-banner" className="p-4 bg-destructive/5 border border-destructive/30 rounded-lg flex flex-col gap-3">
          <div>
            <h4 className="text-sm font-semibold text-destructive mb-1">Tenant Conflict</h4>
            <p className="text-sm text-destructive">
              One or more of the selected tenants is already on another active lease.
              Set their departure date on the old lease before adding them here.
            </p>
          </div>
          <Link
            to="/leases"
            data-testid="tenant-conflict-view-lease"
            className="text-sm text-primary hover:text-primary/80 no-underline"
          >
            View active leases to set departure dates →
          </Link>
          <Button
            type="button"
            variant="outline"
            onClick={generateLease}
            disabled={isLoading}
            className="min-h-[44px]"
          >
            Retry after resolving
          </Button>
        </div>
      )}

      {/* Error state */}
      {error && !generatedDocument && conflictCode !== 'TENANT_ALREADY_ON_ACTIVE_LEASE' && (
        <div data-testid="generation-error" className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
          <h4 className="text-sm font-semibold text-destructive mb-1">Generation Failed</h4>
          <p className="text-sm text-destructive">{error}</p>
          <Button
            type="button"
            variant="destructive"
            onClick={generateLease}
            disabled={isLoading}
            className="mt-3 min-h-[44px]"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && !generatedDocument && (
        <div className="flex flex-col items-center justify-center py-12 bg-accent/50 rounded-lg border border-border">
          <div className="w-10 h-10 border-[3px] border-border border-t-primary rounded-full animate-spin" />
          <p className="mt-4 text-sm font-medium text-foreground">Generating lease documents...</p>
          <p className="text-xs text-muted-foreground mt-1">This may take a moment</p>
        </div>
      )}

      {/* Success state */}
      {generatedDocument && (
        <div data-testid="generation-success" className="flex flex-col gap-4">
          <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
            <h4 className="text-sm font-semibold text-success mb-1">Lease Generated</h4>
            <p className="text-sm text-success">
              Your lease document has been created successfully.
            </p>
          </div>

          {/* Document Summary */}
          <div className="bg-card rounded-lg border border-border p-4">
            <h4 className="text-sm font-semibold text-foreground mb-3">Document Summary</h4>
            <dl className="flex flex-col gap-2">
              <div className="flex justify-between text-sm">
                <dt className="text-muted-foreground">Status</dt>
                <dd>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    documentStatus === 'GENERATED' ? 'bg-primary/10 text-primary' :
                    documentStatus === 'SENT_FOR_SIGNING' ? 'bg-warning/10 text-warning' :
                    documentStatus === 'SIGNED' ? 'bg-success/10 text-success' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {documentStatus}
                  </span>
                </dd>
              </div>
              {generatedDocument.generatedAt && (
                <div className="flex justify-between text-sm">
                  <dt className="text-muted-foreground">Generated At</dt>
                  <dd className="font-medium text-foreground">
                    {formatDateTime(generatedDocument.generatedAt)}
                  </dd>
                </div>
              )}
            </dl>

            {/* Included Documents */}
            <div className="mt-4 pt-4 border-t border-border">
              <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Included Documents
              </h5>
              <ul className="flex flex-col gap-1">
                <li className="text-sm text-foreground flex items-center gap-2">
                  <span className="text-success">•</span> Core Lease
                </li>
                {generatedDocument.selectedDisclosureIds.map(id => (
                  <li key={id} className="text-sm text-foreground flex items-center gap-2">
                    <span className="text-primary">•</span> Disclosure: {id}
                  </li>
                ))}
                {generatedDocument.selectedAddendumSlugs.map(slug => (
                  <li key={slug} className="text-sm text-foreground flex items-center gap-2">
                    <span className="text-accent-foreground">•</span> Addendum: {slug}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Actions */}
          {documentStatus === 'GENERATED' && (
            <div className="flex flex-col gap-2">
              <a
                href={leaseDocumentPdfUrl(generatedDocument.leaseId, generatedDocument.id)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="view-pdf-link"
                className="w-full min-h-[44px] inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-primary bg-primary/5 border border-primary/20 rounded-md hover:bg-primary/10 no-underline"
              >
                View PDF
              </a>
              <Button
                type="button"
                data-testid="send-for-signing-button"
                onClick={sendForSigning}
                disabled={isLoading}
                className="w-full min-h-[44px]"
              >
                {isLoading ? 'Sending...' : 'Send for Signature'}
              </Button>
            </div>
          )}

          {documentStatus === 'SENT_FOR_SIGNING' && (
            <div data-testid="sent-for-signing-banner" className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <p className="text-sm font-medium text-warning">
                Awaiting signatures — the signing envelope has been sent.
              </p>
              <div className="mt-2 flex flex-col gap-1">
                <a
                  href={leaseDocumentPdfUrl(generatedDocument.leaseId, generatedDocument.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="view-pdf-link"
                  className="inline-block text-sm text-warning underline"
                >
                  View PDF
                </a>
                {generatedDocument.signingEnvelopeId && (
                  <a
                    href={`/landlord/signing/${generatedDocument.signingEnvelopeId}`}
                    className="inline-block text-sm text-warning underline"
                  >
                    View Signing Envelope
                  </a>
                )}
              </div>
            </div>
          )}

          {documentStatus === 'SIGNED' && (
            <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
              <p className="text-sm font-medium text-success">Signing complete!</p>
              <div className="mt-2 flex flex-col gap-1">
                <a
                  href={leaseDocumentPdfUrl(generatedDocument.leaseId, generatedDocument.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="view-pdf-link"
                  className="inline-block text-sm text-success underline"
                >
                  View PDF
                </a>
                {generatedDocument.signingEnvelopeId && (
                  <a
                    href={`/landlord/signing/${generatedDocument.signingEnvelopeId}`}
                    className="inline-block text-sm text-success underline"
                  >
                    View Signed Document
                  </a>
                )}
              </div>
            </div>
          )}

          {error && (
            <div data-testid="send-error" className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>
      )}

      {/* Generate Button (shown when not yet generated) */}
      {!generatedDocument && !isLoading && (
        <Button
          type="button"
          data-testid="generate-button"
          onClick={generateLease}
          disabled={disclosuresBlocking}
          aria-disabled={disclosuresBlocking ? 'true' : undefined}
          className="w-full min-h-[44px]"
        >
          Generate Lease
        </Button>
      )}

      {/* Save Draft & Exit */}
      <Button
        type="button"
        variant="outline"
        data-testid="save-draft-button"
        onClick={handleSaveDraftAndExit}
        disabled={isSaving || isLoading}
        className="w-full min-h-[44px]"
      >
        {isSaving ? 'Saving...' : 'Save Draft & Exit'}
      </Button>
    </div>
  );
}
